import QRCode from 'qrcode';

// Set the busy state of the app
export const setBusy = busy => {
    const globalStore = useGlobal();
    if (globalStore.value.busy === busy) return;
    globalStore.value.busy = busy;
};

// Navigate to a path
export const navTo = path => {
    navigateTo(path);
};

// Show modal with message
export const messageModal = (type, title, message) => {
    const globalStore = useGlobal();
    globalStore.value.messageModal.type = type;
    globalStore.value.messageModal.title = title;
    globalStore.value.messageModal.message = message;
    globalStore.value.messageModal.visible = !globalStore.value.messageModal.visible;
};

// Handle theme toggle
export const toggleTheme = () => {
    const globalStore = useGlobal();
    const theme = globalStore.value.theme === 'dark' ? 'light' : 'dark';
    globalStore.value.theme = theme;
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark');
};

// Calculate the percentage of one element compared to another
export const calculatePercentage = (element1, element2, decimalPlaces = 2) => {
    if (!element1 || !element2) return addThousandsSeparator(0, decimalPlaces);
    const percentage = (element1 / element2) * 100;
    return addThousandsSeparator(percentage, decimalPlaces);
};

// Add thousands separator to a number
export const addThousandsSeparator = (number, decimalPlaces = 0) => {
    // Handle edge cases
    if (number === null || number === undefined || isNaN(number)) {
        return (0).toFixed(decimalPlaces);
    }

    // Add thousands separator
    const num = typeof number === 'string' ? parseFloat(number) : number; // Convert to number if string
    const formatted = Math.abs(num).toFixed(decimalPlaces); // Always format with decimal places
    const parts = formatted.split('.'); // Split number into integer and decimal parts
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); // Add thousands separator to integer part
    return `${num < 0 ? '-' : ''}${parts.join('.')}`; // Combine parts and handle negative numbers
};

// Format the market cap
export const formatMarketCap = value => {
    if (!value) return '0';
    if (value >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    return `${(value / 1e6).toFixed(2)}M`;
};

// Get CSS class for price changes
export const getPriceChangeClass = change => {
    if (!change) return 'bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-600';
    return change >= 0 ? 'bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-green-600' : 'bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-red-600';
};

// Format date to human readable
export const formatDate = (dateString, showTime = true) => {
    if (!dateString) return 'Invalid date';
    const date = new Date(dateString);
    if (showTime) {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } else {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }
};

// Format large numbers with commas
export const formatNumber = number => {
    if (!number) return '0';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Format ratio
export const formatRatio = (small, large) => {
    // Input validation
    if (typeof small !== 'number' || typeof large !== 'number') {
        return 0;
    }

    // Handle negative numbers
    const absSmall = Math.abs(small);
    const absLarge = Math.abs(large);

    // Prevent division by zero
    if (absSmall === 0) {
        return 0;
    }

    const ratio = absLarge / absSmall;

    // Return formatted ratio without decimal places
    return ratio.toFixed(0);
};

// Format percentage
export const formatPercentage = percentage => {
    if (!percentage) return '0.00%';
    return `${(percentage * 100).toFixed(2)}%`;
};

// Generate QR code
export const generateQR = async data => {
    try {
        // Generate QR code
        const options = {
            errorCorrectionLevel: 'H',
            margin: 1,
            color: {
                dark: '#000000',
                light: '#ffffff'
            },
            width: 400
        };

        // Return the QR code
        return await QRCode.toDataURL(data, options);
    } catch (err) {
        console.error('Failed to generate QR:', err);
        return '';
    }
};

// Ccount decimal places
export const getDecimalPlaces = number => {
    const match = String(number).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (!match) return 0;
    return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
};

// Delay function
export const delay = ms => {
    if (typeof ms !== 'number' || ms < 0) throw new Error('Invalid delay value');
    return new Promise(resolve => setTimeout(resolve, ms));
};

// Separate the 7, 30, and 90 days price history data
export const separateHistoryData = historyData => {
    // Validate input
    if (!Array.isArray(historyData)) {
        throw new Error('History data must be an array');
    }

    try {
        // Map the data to the correct format
        const formattedData = historyData.map(([timestamp, price]) => {
            if (!timestamp || price === undefined) {
                throw new Error('Invalid data point format');
            }

            // Return the formatted data point
            return {
                x: timestamp,
                y: typeof price === 'number' ? price : parseFloat(price)
            };
        });

        // Filter the data based on the period
        const last7Days = filterPeriod(formattedData, 7);
        const last30Days = filterPeriod(formattedData, 30);
        const last90Days = formattedData; // No need to filter the 90 days data

        // Reduce the 30 days data by doing a modulo operation (keep 1 out of 3 data points)
        const reduced30Days = last30Days.filter((_, index) => index % 3 === 0);

        // Reduce the 90 days data by doing a modulo operation (keep 1 out of 9 data points)
        const reduced90Days = last90Days.filter((_, index) => index % 9 === 0);

        // Return the separated data
        return { last7Days: last7Days, last30Days: reduced30Days, last90Days: reduced90Days };
    } catch (error) {
        console.error('Error processing history data:', error);
        return { last7Days: [], last30Days: [], last90Days: [] };
    }
};

// Filter the price history data based on the period
const filterPeriod = (data, period) => {
    return data.filter(item => {
        const date = new Date(item.x);
        const today = new Date();
        const diffTime = Math.abs(today - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= period;
    }) || [];
};

// Wait for a random amount of time
export const randomWait = async (min = 1000, max = 3000) => {
    const wait = Math.floor(Math.random() * (max - min) + min);
    await new Promise(resolve => setTimeout(resolve, wait));
};

// Fetch data with retry and exponential backoff
export const fetchWithRetry = async (functionToRetry, name, attempt = 1) => {
    const MAX_RETRIES = 3; // Maximum number of retries
    const INITIAL_RETRY_DELAY = 5000; // Initial delay for retry
    const BACKOFF_FACTOR = 2; // Backoff factor for retry

    try {
        const data = await functionToRetry();
        console.log(`Successfully fetched ${name}`);
        return data;
    } catch (error) {
        if (attempt > MAX_RETRIES) {
            console.error(`Failed to fetch ${name} after ${MAX_RETRIES} attempts:`, error);
            return null;
        }

        // Try again with exponential backoff
        const retryDelay = INITIAL_RETRY_DELAY * Math.pow(BACKOFF_FACTOR, attempt - 1);
        console.log(`Retrying ${name} (attempt ${attempt}/${MAX_RETRIES}) after ${retryDelay}ms`);
        await delay(retryDelay);
        return fetchWithRetry(functionToRetry, name, attempt + 1);
    }
};

// Random interval helper (returns milliseconds)
export const getRandomInterval = (intervalHours, varianceHours) => {
    const baseMs = intervalHours * 60 * 60 * 1000;
    const varianceMs = (Math.random() * 2 - 1) * varianceHours * 60 * 60 * 1000;
    return baseMs + varianceMs;
};

// Get meta tags for each page
export const getMetaTags = (options = {}) => {
    const {
        title = 'KaspHub',
        description = 'The best way to get real-time Kaspa cryptocurrency metrics, market analysis and comparative insights.',
        url = '/',
        image = 'https://kasphub.com/images/kasphub-meta-tag-image.png'
    } = options;

    return {
        title: title,
        meta: [
            { charset: 'utf-8' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { name: 'description', content: description },
            { name: 'language', content: 'en' },
            { name: 'keywords', content: 'Kaspa, KaspHub, KAS, cryptocurrency, metrics, market analysis, insights' },

            // Open Graph
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:type', content: 'website' },
            { property: 'og:site_name', content: 'KaspHub' },
            { property: 'og:image', content: image },

            // Twitter Card
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },

            // Robots
            { name: 'robots', content: 'index, follow' }
        ],
        link: [
            { rel: 'canonical', href: `https://kasphub.com${url}` },
        ],
        script: [{
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "KaspHub",
                "url": "https://kasphub.com"
            })
        }]
    };
};